// Go High Level CRM API Service — v2 Private Integration

import type {
  GHLConfig,
  GHLContactPayload,
  GHLContact,
  GHLOpportunityPayload,
  GHLOpportunity,
  GHLApiResponse,
  GHLContactSearchResponse,
  ContactFormSubmission,
  NewsletterSignup,
  FormSubmissionResult,
  CRMEvent
} from '../types/gohighlevel';

class GoHighLevelService {
  private config: GHLConfig;
  private maxRetries = 3;
  private retryDelay = 1000;

  constructor() {
    this.config = {
      baseUrl: process.env.REACT_APP_GHL_BASE_URL || 'https://services.leadconnectorhq.com',
      accessToken: process.env.REACT_APP_GHL_ACCESS_TOKEN || '',
      locationId: process.env.REACT_APP_GHL_LOCATION_ID || '',
      defaultPipelineId: process.env.REACT_APP_GHL_PIPELINE_ID || '',
      version: '2021-07-28'
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('GHL Service Configuration:', {
        baseUrl: this.config.baseUrl,
        hasAccessToken: !!this.config.accessToken,
        tokenPrefix: this.config.accessToken?.substring(0, 4) || 'none',
        hasLocationId: !!this.config.locationId,
        hasPipelineId: !!this.config.defaultPipelineId,
        isConfigured: this.isConfigured()
      });
    }
  }

  isConfigured(): boolean {
    return !!(this.config.accessToken && this.config.locationId);
  }

  private async apiCall<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: any,
    attempt = 1
  ): Promise<GHLApiResponse<T>> {
    if (!this.isConfigured()) {
      return {
        success: false,
        error: 'GHL not configured. Check REACT_APP_GHL_ACCESS_TOKEN and REACT_APP_GHL_LOCATION_ID.'
      };
    }

    try {
      const url = `${this.config.baseUrl}${endpoint}`;
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${this.config.accessToken}`,
          'Version': this.config.version,
          'Content-Type': 'application/json'
        },
        body: body ? JSON.stringify(body) : undefined
      });

      const text = await response.text();
      const data = text ? JSON.parse(text) : null;

      if (!response.ok) {
        if (response.status === 429 && attempt < this.maxRetries) {
          const delay = this.retryDelay * Math.pow(2, attempt - 1);
          console.warn(`GHL rate limited. Retrying in ${delay}ms...`);
          await this.wait(delay);
          return this.apiCall<T>(endpoint, method, body, attempt + 1);
        }

        const errorMessage = Array.isArray(data?.message)
          ? data.message.join(', ')
          : (data?.message || `HTTP ${response.status}: ${response.statusText}`);

        return {
          success: false,
          error: errorMessage,
          meta: { statusCode: response.status }
        };
      }

      return { success: true, data };
    } catch (error) {
      if (attempt < this.maxRetries) {
        const delay = this.retryDelay * Math.pow(2, attempt - 1);
        console.warn(`GHL network error. Retrying in ${delay}ms...`);
        await this.wait(delay);
        return this.apiCall<T>(endpoint, method, body, attempt + 1);
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ── Contact Management ──────────────────────────────────────

  async searchContactsByEmail(email: string): Promise<GHLApiResponse<GHLContactSearchResponse>> {
    const params = `locationId=${this.config.locationId}&query=${encodeURIComponent(email)}`;
    return this.apiCall<GHLContactSearchResponse>(`/contacts/?${params}`, 'GET');
  }

  async getContact(contactId: string): Promise<GHLApiResponse<GHLContact>> {
    const result = await this.apiCall<{ contact: GHLContact }>(`/contacts/${contactId}`, 'GET');
    if (result.success && result.data) {
      return { success: true, data: result.data.contact };
    }
    return { success: false, error: result.error };
  }

  async createContact(contact: GHLContactPayload): Promise<GHLApiResponse<GHLContact>> {
    const payload = { ...contact, locationId: this.config.locationId };
    const result = await this.apiCall<{ contact: GHLContact }>('/contacts/', 'POST', payload);
    if (result.success && result.data) {
      return { success: true, data: result.data.contact };
    }
    return { success: false, error: result.error };
  }

  // v2: locationId must NOT be in PUT payloads (causes 422)
  async updateContact(
    contactId: string,
    updates: Partial<GHLContactPayload>
  ): Promise<GHLApiResponse<GHLContact>> {
    const { locationId, ...cleanUpdates } = updates as any;
    const result = await this.apiCall<{ contact: GHLContact }>(
      `/contacts/${contactId}`, 'PUT', cleanUpdates
    );
    if (result.success && result.data) {
      return { success: true, data: result.data.contact };
    }
    return { success: false, error: result.error };
  }

  async findOrCreateContact(contactData: GHLContactPayload): Promise<GHLApiResponse<GHLContact>> {
    const searchResult = await this.searchContactsByEmail(contactData.email);

    if (!searchResult.success) {
      return { success: false, error: searchResult.error };
    }

    if (searchResult.data && searchResult.data.contacts && searchResult.data.contacts.length > 0) {
      const existing = searchResult.data.contacts[0];
      if (process.env.NODE_ENV === 'development') {
        console.log(`Found existing contact: ${existing.id}`);
      }
      return { success: true, data: existing };
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`Creating new contact: ${contactData.email}`);
    }
    return this.createContact(contactData);
  }

  // ── Opportunity Management ──────────────────────────────────

  async createOpportunity(opportunity: GHLOpportunityPayload): Promise<GHLApiResponse<GHLOpportunity>> {
    const payload = { ...opportunity, locationId: this.config.locationId };
    const result = await this.apiCall<{ opportunity: GHLOpportunity }>(
      '/opportunities/', 'POST', payload
    );
    if (result.success && result.data) {
      return { success: true, data: result.data.opportunity };
    }
    return { success: false, error: result.error };
  }

  async updateOpportunity(
    opportunityId: string,
    updates: Partial<GHLOpportunityPayload>
  ): Promise<GHLApiResponse<GHLOpportunity>> {
    const result = await this.apiCall<{ opportunity: GHLOpportunity }>(
      `/opportunities/${opportunityId}`, 'PUT', updates
    );
    if (result.success && result.data) {
      return { success: true, data: result.data.opportunity };
    }
    return { success: false, error: result.error };
  }

  // ── Notes ───────────────────────────────────────────────────

  async addContactNote(contactId: string, body: string): Promise<GHLApiResponse<any>> {
    return this.apiCall('/contacts/' + contactId + '/notes', 'POST', { body, userId: 'system' });
  }

  // ── Pipelines ───────────────────────────────────────────────

  async getPipelines(): Promise<GHLApiResponse<any>> {
    return this.apiCall(`/opportunities/pipelines?locationId=${this.config.locationId}`);
  }

  // ── Form Submission Handlers ────────────────────────────────

  async submitContactForm(formData: ContactFormSubmission): Promise<FormSubmissionResult> {
    const timestamp = new Date().toISOString();

    try {
      const tags = ['website_lead', 'contact_form'];

      const searchResult = await this.searchContactsByEmail(formData.email);
      let contactId: string;

      if (searchResult.success && searchResult.data?.contacts?.length) {
        // Existing contact — update with latest info and ensure contact_form tag is applied
        const existing = searchResult.data.contacts[0];
        contactId = existing.id;

        await this.updateContact(contactId, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          companyName: formData.company,
          tags
        });
      } else {
        // New contact
        const createResult = await this.createContact({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          companyName: formData.company,
          source: formData.source || 'website_contact_form',
          tags
        });

        if (!createResult.success || !createResult.data) {
          return {
            success: false,
            error: createResult.error || 'Failed to create contact',
            timestamp
          };
        }

        contactId = createResult.data.id;
      }

      // Add message as a contact note so it's visible in GHL
      if (formData.message) {
        try {
          await this.addContactNote(contactId, `Contact form submission:\n\n${formData.message}`);
        } catch (noteError) {
          console.warn('Note addition failed, continuing:', noteError);
        }
      }

      this.logCRMEvent({
        type: 'contact_created',
        data: { contactId, formType: 'contact' },
        timestamp,
        source: formData.source
      });

      return { success: true, contactId, timestamp };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      this.logCRMEvent({
        type: 'error_occurred',
        data: { error: errorMessage, formType: 'contact' },
        timestamp,
        source: formData.source
      });

      return { success: false, error: errorMessage, timestamp };
    }
  }

  async submitNewsletterSignup(signupData: NewsletterSignup): Promise<FormSubmissionResult> {
    const timestamp = new Date().toISOString();

    try {
      const contactPayload: GHLContactPayload = {
        firstName: signupData.firstName || '',
        lastName: signupData.lastName || '',
        email: signupData.email,
        source: signupData.source,
        tags: ['website_lead', 'newsletter_subscriber']
      };

      const contactResult = await this.findOrCreateContact(contactPayload);

      if (!contactResult.success || !contactResult.data) {
        return {
          success: false,
          error: contactResult.error || 'Failed to create contact',
          timestamp
        };
      }

      const contactId = contactResult.data.id;

      this.logCRMEvent({
        type: 'contact_created',
        data: { contactId, formType: 'newsletter' },
        timestamp,
        source: signupData.source
      });

      return { success: true, contactId, timestamp };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      this.logCRMEvent({
        type: 'error_occurred',
        data: { error: errorMessage, formType: 'newsletter' },
        timestamp,
        source: signupData.source
      });

      return { success: false, error: errorMessage, timestamp };
    }
  }

  // ── Utilities ───────────────────────────────────────────────

  private logCRMEvent(event: CRMEvent): void {
    if (process.env.NODE_ENV === 'development') {
      console.log('CRM Event:', event);
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('crm-event', { detail: event }));
    }
  }

  getServiceTypePipeline(serviceType: string): string {
    const pipelineMap: Record<string, string> = {
      'daisy': process.env.REACT_APP_GHL_DAISY_PIPELINE || this.config.defaultPipelineId,
      'daisy_agency': process.env.REACT_APP_GHL_DAISY_AGENCY_PIPELINE || this.config.defaultPipelineId,
      'flourish': process.env.REACT_APP_GHL_FLOURISH_PIPELINE || this.config.defaultPipelineId,
      'ai_consulting': process.env.REACT_APP_GHL_CONSULTING_PIPELINE || this.config.defaultPipelineId,
      'training': process.env.REACT_APP_GHL_TRAINING_PIPELINE || this.config.defaultPipelineId
    };
    return pipelineMap[serviceType] || this.config.defaultPipelineId;
  }

  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.apiCall('/locations/' + this.config.locationId);
      return response.success;
    } catch {
      return false;
    }
  }
}

export const ghlService = new GoHighLevelService();
export default ghlService;
